// Turning a member's seven chosen colours into the twenty-eight the app paints
// with, and then into CSS.
//
// Why derivation
// --------------
// styles/themes.css defines a contract of 28 variables per colour scheme, and
// every built-in palette spells all of them out by hand. Asking a person to fill
// in 28 colours twice — once for light, once for dark — is not a feature, it is
// a chore, and most of those variables are not really choices: a border is the
// surface nudged toward the text, muted text is the text nudged toward the
// surface, a scrim is the darkest thing in the palette at 55%.
//
// So a custom palette stores seven seeds per scheme and this module computes the
// rest. The seeds are what someone actually has an opinion about; everything
// else follows from them. Storing seeds rather than the finished set also means
// improving a formula here improves every palette anyone has already saved,
// instead of leaving them frozen at whatever the formula was that afternoon.
//
// Safety
// ------
// Everything this module emits ends up inside a <style> element. Two things keep
// that from being an injection: the server stores nothing but canonical
// `#rrggbb` (see model.Palette.Normalize in Go), and every value below is
// *computed* — parsed into numbers and re-serialised — so a string that somehow
// arrived malformed cannot survive the round trip into the output.

import type { CustomTheme, Gradient, PaletteVariant } from "../api/types";

/** A colour scheme. Dark is chosen by the OS, never by the app. */
export type Scheme = "light" | "dark";

interface RGB {
  r: number;
  g: number;
  b: number;
}

// --- colour maths ------------------------------------------------------------

/**
 * Parses `#rrggbb` or `#rgb`. Returns null for anything else, which is the
 * signal to fall back rather than to emit something unparsed.
 */
function parseHex(hex: string): RGB | null {
  const m = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(hex.trim());
  const captured = m?.[1];
  if (!captured) return null;

  const digits =
    captured.length === 3
      ? captured
          .split("")
          .map((d) => d + d)
          .join("")
      : captured;

  return {
    r: parseInt(digits.slice(0, 2), 16),
    g: parseInt(digits.slice(2, 4), 16),
    b: parseInt(digits.slice(4, 6), 16),
  };
}

const clampByte = (n: number) => Math.max(0, Math.min(255, Math.round(n)));

function toHex({ r, g, b }: RGB): string {
  const pair = (n: number) => clampByte(n).toString(16).padStart(2, "0");
  return `#${pair(r)}${pair(g)}${pair(b)}`;
}

/** Linear blend. t=0 is `a`, t=1 is `b`. */
function mix(a: RGB, b: RGB, t: number): RGB {
  return {
    r: a.r + (b.r - a.r) * t,
    g: a.g + (b.g - a.g) * t,
    b: a.b + (b.b - a.b) * t,
  };
}

const BLACK: RGB = { r: 0, g: 0, b: 0 };
const WHITE: RGB = { r: 255, g: 255, b: 255 };

const darken = (c: RGB, t: number) => mix(c, BLACK, t);

/** `#rrggbbaa`. Alpha as hex keeps every emitted colour one grammar wide. */
function withAlpha(c: RGB, alpha: number): string {
  const a = clampByte(alpha * 255)
    .toString(16)
    .padStart(2, "0");
  return `${toHex(c)}${a}`;
}

/** WCAG relative luminance, used only to choose between two candidates. */
function luminance({ r, g, b }: RGB): number {
  const channel = (v: number) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

function contrast(a: RGB, b: RGB): number {
  const la = luminance(a);
  const lb = luminance(b);
  const hi = Math.max(la, lb);
  const lo = Math.min(la, lb);
  return (hi + 0.05) / (lo + 0.05);
}

/**
 * Picks the ink that reads best on every one of `backgrounds`.
 *
 * Plural because some variables sit on more than one surface: `--c-on-bucket`
 * is the label on both the Spend and the Save card, so the right answer is the
 * candidate whose *worst* contrast is best, not the one that wins on average.
 */
function readableOn(backgrounds: RGB[], ink: RGB, paper: RGB): RGB {
  const worst = (candidate: RGB) =>
    Math.min(...backgrounds.map((bg) => contrast(candidate, bg)));
  return worst(ink) >= worst(paper) ? ink : paper;
}

// --- the palette contract ----------------------------------------------------

/**
 * Positive and negative are not derived from the brand.
 *
 * themes.css asks that "good" and "careful" keep reading as good and careful
 * whatever the brand hue is doing, and a green computed from a red brand is not
 * green. These are the shipped palette's values, which every built-in theme also
 * uses unchanged.
 */
const SEMANTIC: Record<Scheme, { positive: string; negative: string }> = {
  light: { positive: "#17845c", negative: "#c2455b" },
  dark: { positive: "#4ecb96", negative: "#ff7b91" },
};

/** The gold in the confetti is the same in every palette. */
const CONFETTI_GOLD = "#ffd166";

/** Fallbacks, used only when a colour fails to parse — see deriveVars. */
const FALLBACK: Record<Scheme, PaletteVariant> = {
  light: {
    brand: "#5b6cff",
    spend: "#f4a261",
    save: "#2a9d8f",
    celebrate: "#16a06a",
    surface: "#ffffff",
    text: "#1a1f2e",
    background: {
      angle: 160,
      stops: [
        { color: "#f6f7fb", position: 0 },
        { color: "#eef0ff", position: 100 },
      ],
    },
  },
  dark: {
    brand: "#8b98ff",
    spend: "#f4a261",
    save: "#2a9d8f",
    celebrate: "#1fb87c",
    surface: "#1c1f2b",
    text: "#eef0f6",
    background: {
      angle: 160,
      stops: [
        { color: "#14161f", position: 0 },
        { color: "#1b1f33", position: 100 },
      ],
    },
  },
};

/**
 * Renders a gradient as a CSS value.
 *
 * Every number is coerced and clamped rather than trusted: this string is
 * interpolated into a stylesheet, and a NaN angle would otherwise emit
 * `NaNdeg` and silently void the whole declaration.
 */
export function gradientCss(gradient: Gradient, fallback: Gradient): string {
  const stops = gradient.stops?.length >= 2 ? gradient.stops : fallback.stops;
  const angle = Number.isFinite(gradient.angle)
    ? Math.max(0, Math.min(360, Math.round(gradient.angle)))
    : fallback.angle;

  const rendered = stops.map((stop) => {
    const color = parseHex(stop.color) ?? BLACK;
    const position = Number.isFinite(stop.position)
      ? Math.max(0, Math.min(100, Math.round(stop.position)))
      : 0;
    return `${toHex(color)} ${position}%`;
  });

  return `linear-gradient(${angle}deg, ${rendered.join(", ")})`;
}

/** The range a card opacity percentage can occupy. Mirrors the Go model. */
export const MIN_CARD_OPACITY = 0;
export const MAX_CARD_OPACITY = 100;

/**
 * Where the editor's slider stops.
 *
 * A UI judgement, not a rule: text on a card stays fully opaque at any setting,
 * but past a point the background wins and the words stop being readable —
 * worst on a busy multi-stop palette. A hand-edited import may go lower and is
 * taken at its word, which is why the server clamps only to 0..100.
 */
export const CARD_OPACITY_SLIDER_MIN = 40;

/**
 * The card's background colour, with alpha when the palette asked for it.
 *
 * `undefined` means the palette never chose one — distinct from 0, which is a
 * deliberately see-through card. Unset returns plain opaque hex, exactly the
 * string emitted before this feature existed, which is what keeps the shipped
 * built-ins byte-identical.
 */
function cardBackground(surface: RGB, opacity: number | undefined): string {
  if (opacity === undefined) return toHex(surface);

  const clamped = Math.min(
    MAX_CARD_OPACITY,
    Math.max(MIN_CARD_OPACITY, Math.round(opacity)),
  );
  // Fully opaque is emitted as six digits rather than "…ff", so a palette
  // explicitly set to 100 is indistinguishable from one that never chose.
  if (clamped >= MAX_CARD_OPACITY) return toHex(surface);

  return withAlpha(surface, clamped / 100);
}

/**
 * Expands one variant's seeds into the full variable contract.
 *
 * The comments below say what each derivation is *for*; the constants are
 * tuned so that feeding in the shipped Blueberry seeds reproduces Blueberry to
 * within a shade or two, which is the only calibration that matters.
 */
export function deriveVars(
  variant: PaletteVariant,
  scheme: Scheme,
): Record<string, string> {
  const fallback = FALLBACK[scheme];
  // A seed that will not parse is replaced individually rather than failing the
  // whole palette: one bad colour should cost one colour, not the theme.
  const seed = (value: string, fallbackValue: string): RGB =>
    parseHex(value) ?? parseHex(fallbackValue) ?? BLACK;

  const brand = seed(variant.brand, fallback.brand);
  const spend = seed(variant.spend, fallback.spend);
  const save = seed(variant.save, fallback.save);
  const celebrate = seed(variant.celebrate, fallback.celebrate);
  const surface = seed(variant.surface, fallback.surface);
  const text = seed(variant.text, fallback.text);

  // Ink and paper for every "what goes on top of this?" decision. Derived from
  // the palette's own text colour rather than pure black and white, so a warm
  // palette stays warm all the way down to its labels.
  const ink = darken(text, 0.15);
  const paper = mix(surface, WHITE, 0.85);

  const semantic = SEMANTIC[scheme];
  const positive = seed(semantic.positive, semantic.positive);
  const negative = seed(semantic.negative, semantic.negative);

  const background = variant.background ?? fallback.background;
  const firstStop = parseHex(background.stops?.[0]?.color ?? "") ?? surface;

  return {
    // Brand.
    "--c-brand": toHex(brand),
    "--c-brand-dark": toHex(darken(brand, 0.18)),
    // A wash of the brand over the surface, for chips and quiet highlights.
    "--c-brand-soft": toHex(mix(brand, surface, 0.88)),
    "--c-on-brand": toHex(readableOn([brand], ink, paper)),

    // Buckets. Give is legacy — kept only so rows written before migration
    // 0003 still render — so it is simply the midpoint of the two real
    // buckets, which is always harmonious and never has to be chosen.
    "--c-spend": toHex(spend),
    "--c-save": toHex(save),
    "--c-give": toHex(mix(spend, save, 0.5)),
    "--c-custom": toHex(mix(text, surface, 0.45)),
    // One label sits on both bucket cards, so it has to clear both.
    "--c-on-bucket": toHex(readableOn([spend, save], ink, paper)),

    // Money.
    "--c-positive": toHex(positive),
    "--c-negative": toHex(negative),
    "--c-on-danger": toHex(readableOn([negative], ink, paper)),

    // Celebration.
    "--c-celebrate": toHex(celebrate),
    "--c-celebrate-deep": toHex(darken(celebrate, 0.22)),
    "--c-celebrate-glow": withAlpha(celebrate, 0.45),
    "--c-on-celebrate": toHex(readableOn([celebrate], ink, paper)),
    "--c-confetti-1": CONFETTI_GOLD,
    "--c-confetti-2": toHex(mix(surface, WHITE, 0.9)),
    "--c-confetti-3": toHex(mix(celebrate, WHITE, 0.5)),

    // Surfaces. --c-bg is the flat colour behind the gradient: it is what any
    // rule reading a background *colour* gets, and what shows if the gradient
    // fails to paint.
    "--c-bg": toHex(firstStop),
    "--c-bg-image": gradientCss(background, fallback.background),
    "--c-surface": toHex(surface),
    // What a card is painted with. Separate from --c-surface, which a dozen
    // other things use, so making cards see-through cannot quietly turn modals
    // and inputs see-through with them. Only the background takes the alpha —
    // the text on a card is a different property and stays fully opaque.
    "--c-card-bg": cardBackground(surface, variant.cardOpacity),
    "--c-surface-alt": toHex(mix(surface, text, 0.055)),
    "--c-border": toHex(mix(surface, text, 0.16)),
    // A scrim's whole job is to darken the page behind a modal, so it has to
    // anchor to black rather than to the palette. Deriving it from `text` broke
    // in dark schemes, where text is *light*: darkening it 50% still left a
    // mid-tone, and a palette with a warm text colour got a visibly orange
    // wash instead of a dim one. Mixing the surface most of the way to black
    // lands dark in both schemes while keeping a trace of the palette's hue.
    "--c-scrim": withAlpha(mix(surface, BLACK, 0.88), 0.55),

    // Text.
    "--c-text": toHex(text),
    "--c-text-soft": toHex(mix(text, surface, 0.3)),
    "--c-text-faint": toHex(mix(text, surface, 0.52)),
  };
}

// --- CSS emission ------------------------------------------------------------

/**
 * The `data-theme` value that selects this palette. Matches the server's
 * model.CustomThemePrefix.
 */
export function customThemeRef(id: string): string {
  return `custom:${id}`;
}

/** IDs are server-issued UUIDs; anything else never reaches a selector. */
const SAFE_ID = /^[0-9a-fA-F-]{1,64}$/;

function declarations(vars: Record<string, string>): string {
  return Object.entries(vars)
    .map(([name, value]) => `${name}:${value}`)
    .join(";");
}

/**
 * Renders one custom palette as a pair of CSS rules.
 *
 * A `<style>` element rather than inline styles on <html>, for one reason that
 * decides it: inline styles cannot carry a media query, and dark mode here is a
 * `prefers-color-scheme` query and nothing else. The app has no runtime notion
 * of which scheme is active — no matchMedia listener, no toggle — and this keeps
 * it that way, with the browser switching variants on its own exactly as it does
 * for the built-in palettes.
 *
 * The selector is unscoped (`[data-theme=…]`, not `:root[data-theme=…]`) for the
 * same reason themes.css is: it lets the picker paint a swatch in a palette that
 * is not the active one by putting the attribute on the swatch itself.
 */
export function customThemeCss(theme: CustomTheme): string {
  if (!SAFE_ID.test(theme.id)) return "";

  const selector = `[data-theme="${customThemeRef(theme.id)}"]`;
  const light = declarations(deriveVars(theme.palette.light, "light"));
  const dark = declarations(deriveVars(theme.palette.dark, "dark"));

  return (
    `${selector}{${light}}` +
    `@media (prefers-color-scheme: dark){${selector}{${dark}}}`
  );
}

/**
 * The stylesheet for every palette a member owns — not just the active one, so
 * the picker can preview all of them at once.
 */
export function customThemesCss(themes: CustomTheme[]): string {
  return themes.map(customThemeCss).join("\n");
}
